import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Avatar,
  Box,
  Chip,
  Stack,
  Paper,
  Divider,
  IconButton,
  Grid,
  LinearProgress,
  colors,
} from "@mui/material";
import {
  Person,
  Email,
  Today,
  Assignment,
  MoreVert,
  Circle as CircleIcon,
} from "@mui/icons-material";
import useUserId from "hooks/useUserId";
import ScoreMatching from "./ScoreMatching";
import { COLORS } from "colors/color";
import { PostSearchBar } from "components/searchBars/SearchBars";
import { SearchButton } from "components/Buttons/Buttons";
import NoPostApplied from "./NopostApplied";
import { useTranslation } from "react-i18next"; // Import the translation hook

const CandidateProfile = ({ candidate_id }) => {
  const { t } = useTranslation(); // Initialize the translation hook
  const [candidateProfile, setCandidateProfile] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;
  const userId = useUserId();
  const [filter, setFilter] = useState("");

  useEffect(() => {
    const fetchCandidateInfos = async () => {
      try {
        const response = await fetch(
          `${apiUrl}recruiters/${userId.userId}/${candidate_id}/candidates-applications/`
        );

        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setCandidateProfile(data);
      } catch (error) {
        console.error("Failed to fetch candidate info:", error);
      }
    };

    if (candidate_id) fetchCandidateInfos();
  }, [candidate_id, apiUrl, userId]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (!candidateProfile) return <LinearProgress sx={{ width: "100%" }} />;
  const filteredApplications = candidateProfile.applications.filter(
    (application) =>
      application.post.title.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Paper
        elevation={1}
        sx={{
          p: 4,
          background: "linear-gradient(135deg, #e0f7fa 0%, #ffffff 100%)",
          borderRadius: 4,
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Avatar
              sx={{
                width: 72,
                height: 72,
                bgcolor: "primary.main",
                boxShadow: 3,
              }}
              src={candidateProfile.candidate.image}
              alt={candidateProfile.candidate.name}
            >
              {!candidateProfile.candidate.image && (
                <Person sx={{ fontSize: 36 }} />
              )}
            </Avatar>
          </Grid>
          <Grid item xs>
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontFamily: "Poppins",
              }}
            >
              {candidateProfile.candidate.name.charAt(0).toUpperCase() +
                candidateProfile.candidate.name.slice(1)}
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              <Email fontSize="small" color="action" />
              <Typography
                color="text.secondary"
                sx={{
                  fontFamily: "Poppins",
                }}
              >
                {candidateProfile.candidate.email}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
        <Box
          sx={{
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            px: 1,
            mt: 2,
            borderRadius: "10px",
            width: "100%",
          }}
        >
          <PostSearchBar
            variant="outlined"
            size="small"
            type="text"
            placeholder={t("filterPlaceholder")} // Translated placeholder
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ flex: 1, mr: 1 }}
          />
        </Box>
      </Paper>

      <Stack spacing={3} mt={3}>
        {filteredApplications.length > 0 ? (
          filteredApplications.map((application) => (
            <Card
              key={application.id}
              sx={{
                width: "100%",
                borderRadius: 3,
                boxShadow: "0 6px 15px rgba(0,0,0,0.1)",
                backgroundColor: "background.paper",
                transition: "transform 0.3s, box-shadow 0.3s",
                ":hover": {
                  transform: "scale(1.005)",
                  boxShadow: "0px 8px 25px rgba(0,0,0,0.2)",
                },
              }}
            >
              <CardContent>
                <Stack spacing={2}>
                  <Box>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                          fontFamily: "Poppins",
                          textTransform: "none",
                        }}
                      >
                        {application.post.title.charAt(0).toUpperCase() +
                          application.post.title.slice(1)}
                      </Typography>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        mt={3}
                        mr={3}
                      >
                        <ScoreMatching score={application.score_matching} />
                      </Stack>
                    </Stack>
                    <Chip
                      icon={<Today fontSize="small" />}
                      label={formatDate(application.date)}
                      size="small"
                      variant="outlined"
                      sx={{ borderRadius: 1 }}
                    />
                  </Box>

                  {application.steps.map((step, stepIndex) => (
                    <Box key={stepIndex}>
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        mb={1}
                      >
                        <Avatar
                          sx={{
                            bgcolor: COLORS.button_color,
                            width: 48,
                            height: 48,
                          }}
                        >
                          <Assignment />
                        </Avatar>
                        <Typography
                          variant="subtitle1"
                          fontWeight="medium"
                          sx={{
                            fontFamily: "Poppins",
                            textTransform: "none",
                          }}
                        >
                          {t("stepLabel")}: {step.name} {/* Translated label */}
                        </Typography>
                      </Stack>

                      <Stack spacing={1.5} sx={{ pl: 6 }}>
                        {step.notes.map((note, noteIndex) => (
                          <Paper
                            key={noteIndex}
                            sx={{
                              p: 2,
                              bgcolor: "grey.50",
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: "grey.200",
                              boxShadow: "0px 2px 6px rgba(0,0,0,0.1)",
                            }}
                          >
                            <Typography sx={{ mb: 1, fontFamily: "Poppins" }}>
                              {t("noteLabel")}: {note.note} {/* Translated label */}
                            </Typography>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              sx={{
                                pt: 1,
                                borderTop: "1px solid",
                                borderColor: "grey.200",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="primary"
                                sx={{
                                  fontFamily: "Poppins",
                                }}
                              >
                                {t("recruiterLabel")}: {note.recruiter} {/* Translated label */}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatDate(note.date)}
                              </Typography>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                      {stepIndex < application.steps.length - 1 && (
                        <Divider sx={{ my: 3 }} />
                      )}
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ))
        ) : (
          <NoPostApplied message={t("noPostApplied")} /> 
        )}
      </Stack>
    </Box>
  );
};

export default CandidateProfile;